import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth0, User, RedirectLoginOptions } from '@auth0/auth0-react';
import { MissingRefreshTokenError } from "@auth0/auth0-spa-js";
import { syncAuth0User, verifyInviteCode } from '../services/User';
import config from '../config/api';
import logger from '../logging/logger';

interface AuthContextType {
  user: User | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  error: Error | undefined;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  getToken: () => Promise<string | null>;
  userUuid: string | null;
  userEmail: string | null;
  waitlistApproved: boolean;
  submitInviteCode: (code: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth0 = useAuth0();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [userSynced, setUserSynced] = React.useState(false);
  // Set when the user is authenticated at Auth0 but the app session cannot be
  // established (sync, token, or profile fetch failed). Blocks isAuthenticated
  // and is surfaced to the user instead of failing silently downstream.
  const [authError, setAuthError] = React.useState<Error | null>(null);
  const [userUuid, setUserUuid] = React.useState<string | null>(null);
  const [waitlistApproved, setWaitlistApproved] = React.useState(false);
  const syncAttemptRef = React.useRef(0);

  const MAX_SYNC_RETRIES = 3;
  const SYNC_RETRY_DELAY_MS = 2000;

  const handleLoginWithRedirect = React.useCallback(
    async (options?: RedirectLoginOptions) => {
      await auth0.loginWithRedirect({
        ...options,
        authorizationParams: {
          ...options?.authorizationParams,
          prompt: options?.authorizationParams?.prompt ?? 'select_account',
        },
      });
    },
    [auth0]
  );

  // Sync user to database when authenticated
  React.useEffect(() => {
    if (auth0.isAuthenticated && auth0.user && !auth0.isLoading && !userSynced && !authError) {
      const syncUser = async () => {
        for (let attempt = 1; attempt <= MAX_SYNC_RETRIES; attempt++) {
          try {
            logger.info(`🔄 Starting user sync (attempt ${attempt}/${MAX_SYNC_RETRIES})...`);
            const result = await syncAuth0User(
              auth0.user?.email || '',
              auth0.user?.name || '',
              auth0.user?.sub || ''
            );
            logger.info('✅ User synced to database');
            syncAttemptRef.current = 0;
            setUserSynced(true);
            if (result.data?.waitlist_approved) {
              setWaitlistApproved(true);
            }
            return;
          } catch (error) {
            logger.error(`❌ Failed to sync user (attempt ${attempt}/${MAX_SYNC_RETRIES}):`, error);
            if (attempt < MAX_SYNC_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, SYNC_RETRY_DELAY_MS * attempt));
            }
          }
        }
        logger.error('❌ User sync failed after all retries');
        setAuthError(new Error('Could not sign you in — the server was unavailable. Please try again.'));
      };

      syncUser();
    }
  }, [auth0.isAuthenticated, auth0.user, auth0.isLoading, userSynced, authError]);

  // Get token with error handling
  // Note: We intentionally list specific auth0 properties rather than the whole object
  // to avoid unnecessary re-renders when other auth0 properties change
  const getToken = React.useCallback(async (): Promise<string | null> => {
    if (!auth0.isAuthenticated || !userSynced) {
      logger.warn('Cannot get token: user not authenticated or not synced');
      return null;
    }

    try {
      const token = await auth0.getAccessTokenSilently();
      return token;
    } catch (e: any) {
      logger.error("❌ Failed to get token:", e);

      const code = e?.error; // GenericError.error in auth0-spa-js :contentReference[oaicite:2]{index=2}

      if (
        e instanceof MissingRefreshTokenError ||
        code === "missing_refresh_token" ||
        code === "login_required" ||
        code === "consent_required"
      ) {
        await handleLoginWithRedirect();
      }

      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0.isAuthenticated, auth0.getAccessTokenSilently, handleLoginWithRedirect, userSynced]);

  // Fetch user UUID when user is synced
  React.useEffect(() => {
    if (auth0.isAuthenticated && auth0.user && userSynced && !userUuid && !authError) {
      const fetchUserUuid = async () => {
        try {
          const token = await getToken();
          if (!token) {
            setAuthError(new Error('Could not obtain an access token. Please log in again.'));
            return;
          }

          const userResponse = await fetch(`${config.apiUrl}/user/email/${encodeURIComponent(auth0.user!.email!)}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!userResponse.ok) {
            throw new Error(`Failed to load user profile (status ${userResponse.status})`);
          }

          const userData = await userResponse.json();
          setUserUuid(userData.uuid);
          logger.info('✅ User UUID cached:', userData.uuid);
        } catch (error) {
          logger.error('❌ Failed to fetch user UUID:', error);
          setAuthError(error instanceof Error ? error : new Error('Failed to load user profile.'));
        }
      };

      fetchUserUuid();
    }
  }, [auth0.isAuthenticated, auth0.user, userSynced, userUuid, authError, getToken]);

  const handleLogout = (options?: { logoutParams?: { returnTo?: string } }) => {
    setIsLoggingOut(true);
    setUserSynced(false);
    setUserUuid(null);
    setAuthError(null);
    setWaitlistApproved(false);
    
    // Clear ALL Auth0 related storage to prevent auto-login
    // Auth0 stores tokens with keys starting with '@@auth0spajs@@'
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('@@auth0spajs@@') || key.includes('auth0')) {
        localStorage.removeItem(key);
      }
    });
    
    // Also clear sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('@@auth0spajs@@') || key.includes('auth0')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Logout and redirect to Auth0's logout endpoint
    const returnTo = options?.logoutParams?.returnTo || window.location.origin;
    auth0.logout({
      logoutParams: {
        returnTo,
      },
    });
  };

  const submitInviteCode = React.useCallback(async (code: string): Promise<boolean> => {
    if (!auth0.user?.email) return false;
    const ok = await verifyInviteCode(auth0.user.email, code);
    if (ok) setWaitlistApproved(true);
    return ok;
  }, [auth0.user?.email]);

  const contextValue: AuthContextType = {
    user: auth0.user,
    // Authenticated means a usable app identity exists — userUuid is only set
    // after sync succeeds AND a token is obtained, so it proves both.
    isAuthenticated: auth0.isAuthenticated && !isLoggingOut && !!userUuid,
    // Stay in loading until the identity resolves (userUuid) or terminally fails.
    isLoading: auth0.isLoading || (auth0.isAuthenticated && !isLoggingOut && !userUuid && !authError),
    isLoggingOut,
    error: authError ?? auth0.error,
    loginWithRedirect: handleLoginWithRedirect,
    logout: handleLogout,
    getToken,
    userUuid,
    userEmail: auth0.user?.email ?? null,
    waitlistApproved,
    submitInviteCode,
  };

  // Debug all Auth0 state changes
  React.useEffect(() => {
    logger.info('🔐 AuthContext - Complete state:', {
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading,
      hasUser: !!auth0.user,
      userEmail: auth0.user?.email,
      error: auth0.error,
      errorMessage: auth0.error?.message,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });

    if (auth0.error) {
      logger.error('❌ Auth0 Error:', auth0.error);
    }
  }, [auth0.isAuthenticated, auth0.isLoading, auth0.user, auth0.error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};